import { useState } from 'react';
import { useFormConfigStore, resolveV3, activeForm as resolveActiveForm } from '../state/store';
import { useFieldValidator } from '../hooks/useFieldValidator';
import { FieldRow } from './FieldRow';
import { FieldActions } from './FieldActions';
import { FieldCreateModal } from './FieldCreateModal';

const DATA_TYPES = ['all', 'text', 'number', 'decimal', 'date', 'boolean'] as const;

interface FieldListPaneProps {
  /**
   * The form currently in scope, owned by `FormConfigPage` (URL `?form=` →
   * store `activeFormId`). The pane shows ONLY this form's fields — every field
   * is form-owned (Spec A §5 clone-and-diverge: no shared/inherited job-type
   * field layer). `null` resolves to the default form via the same fallback the
   * store's `activeForm` uses.
   */
  activeFormId: string | null;
}

export function FieldListPane({ activeFormId }: FieldListPaneProps) {
  const [typeFilter, setTypeFilter] = useState<typeof DATA_TYPES[number]>('all');
  const [scopeFilter, setScopeFilter] = useState<'record' | 'batch'>('record');
  const [batchFieldPromptOpen, setBatchFieldPromptOpen] = useState(false);

  // Source of truth is `contentV3`. Mirror the store's `resolveV3`: when only
  // the derived legacy `content` is seeded (test/pre-load paths), migrate it on
  // the fly so the pane reads correctly regardless of seeding path.
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const legacyContent = useFormConfigStore((s) => s.content);
  const selectedFieldName = useFormConfigStore((s) => s.selectedFieldName);
  const setSelectedField = useFormConfigStore((s) => s.setSelectedField);
  const openFieldEditor = useFormConfigStore((s) => s.openFieldEditor);
  const addField = useFormConfigStore((s) => s.addField);
  const addBatchField = useFormConfigStore((s) => s.addBatchField);
  const duplicateField = useFormConfigStore((s) => s.duplicateField);
  const deleteField = useFormConfigStore((s) => s.deleteField);
  const { errors } = useFieldValidator();

  // Resolve the V3 blob and the active form via the SAME helpers the store's
  // mutating actions use (`resolveV3` + `activeForm`), so the read path and the
  // write path can never drift on how a stale/null activeFormId resolves.
  // `resolveV3` migrates legacy `content` on the fly for test/pre-load paths;
  // `activeForm` falls back to the default form when activeFormId matches none.
  const v3 = resolveV3({ contentV3, content: legacyContent });
  if (!v3) return null;
  const activeForm = resolveActiveForm(v3, activeFormId);

  const switchScope = (next: 'record' | 'batch') => {
    setScopeFilter(next);
    setSelectedField(null);
  };

  // Every field is owned by the active form. Filter to the chosen scope, then
  // by data type.
  const scopedFields = activeForm.fields
    .filter((f) => f.scope === scopeFilter)
    .filter((f) => typeFilter === 'all' || f.dataType === typeFilter);

  const existingBatchFieldNames = new Set(
    activeForm.fields.filter((f) => f.scope === 'batch').map((f) => f.name),
  );

  return (
    <div className="field-list-pane" data-field-list-pane>
      <div className="field-list-pane__header">
        <div className="form-toggle-group field-list-pane__scope-toggle">
          <button
            type="button"
            aria-label="Document Fields"
            className={`form-toggle-group__item ${scopeFilter === 'record' ? 'is-active' : ''}`}
            onClick={() => switchScope('record')}
          >
            Document Fields
          </button>
          <button
            type="button"
            aria-label="Batch Fields"
            className={`form-toggle-group__item ${scopeFilter === 'batch' ? 'is-active' : ''}`}
            onClick={() => switchScope('batch')}
          >
            Batch Fields
          </button>
        </div>
        <label>
          Filter by type
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as typeof DATA_TYPES[number])}>
            {DATA_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        {scopeFilter === 'record' ? (
          <FieldActions
            selectedFieldName={selectedFieldName}
            fieldCount={activeForm.fields.length}
            onAdd={(fieldBase) => {
              // A new field belongs to the active form by construction — the
              // store's addField targets the active form via patchActiveForm.
              addField(fieldBase);
              // R-152 — open the drawer immediately so IdentitySection's
              // commitLabel can enforce the empty-label rejection (R-141).
              // Without this, the operator can publish with an empty label,
              // re-introducing the "every field reads 'New Field'" bug.
              openFieldEditor(fieldBase.name);
            }}
            onDuplicate={duplicateField}
            onDelete={deleteField}
          />
        ) : (
          <div className="field-actions">
            <button type="button" onClick={() => setBatchFieldPromptOpen(true)}>Add batch field</button>
          </div>
        )}
      </div>
      <div className="field-list-pane__columns">
        <span className="col-name" aria-hidden="true">Name</span>
        <span className="col-label" aria-hidden="true">Label</span>
        <span className="col-type" aria-hidden="true">Type</span>
        <span className="col-scope" aria-hidden="true">Scope</span>
        <span className="col-stages" aria-hidden="true">Stages</span>
        {/* R-169 (2026-05-26) — operator-flagged: the bare "!" header
            was too subtle to read as "validation errors." Swap to ⚠
            with a tooltip the operator can hover to confirm. The cell
            itself isn't interactive at the header level (each row's
            error cell is the click target — see FieldRow). */}
        <span
          className="col-errors"
          aria-label="Validation errors"
          title="Field has validation errors"
        >
          ⚠
        </span>
      </div>
      <ul className="field-list-pane__list">
        {scopedFields.map((f) => (
          <li key={f.fieldDefinitionId}>
            <FieldRow
              field={f}
              selected={selectedFieldName === f.name}
              errorCount={(errors.get(f.name) ?? []).filter((e) => e.severity === 'error').length}
              onSelect={setSelectedField}
              onEdit={openFieldEditor}
              onDelete={deleteField}
              onDuplicate={duplicateField}
              variant="editable"
              headerMapping={v3.headerMapping}
            />
          </li>
        ))}
      </ul>
      <FieldCreateModal
        open={batchFieldPromptOpen}
        title="Add batch field"
        description="Batch fields hold values that apply to the whole batch, like totals or document counts."
        placeholder="e.g. invoice_total"
        defaultDataType="number"
        validate={(name) =>
          existingBatchFieldNames.has(name)
            ? `A batch field named "${name}" already exists`
            : null
        }
        onConfirm={(name, dataType) => {
          addBatchField(name, dataType);
          setBatchFieldPromptOpen(false);
        }}
        onCancel={() => setBatchFieldPromptOpen(false)}
      />
    </div>
  );
}
